import { withUtm } from "@/lib/utmAttribution";
import { uiText } from "@/lib/i18n/uiText";
import { Moon, Sun, LogIn, LogOut, Menu, UserRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { forwardRef, useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "@/lib/router-compat";
import { useTranslation } from "react-i18next";
import { authPath } from "@/lib/authRedirect";
import { TopNav } from "@/components/layout/TopNav";
import { Logo } from "@/components/brand/Logo";
import { NotificationBell } from "@/components/notifications/NotificationBell";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { TimezoneSelector } from "@/components/settings/TimezoneSelector";
import { CoinBalance } from "@/components/credits/CoinBalance";
import { DailyStreak } from "@/components/credits/DailyStreak";
import { InstallAppButton } from "@/components/pwa/InstallAppButton";
import { LanguageSwitcher } from "@/components/i18n/LanguageSwitcher";
import { LanguageErrorBoundary } from "@/components/i18n/LanguageErrorBoundary";
import { GlobalSearchTrigger } from "@/components/search/GlobalSearchDialog";
import { ChatButton } from "@/components/support/ChatButton";


interface HeaderProps extends React.HTMLAttributes<HTMLElement> {
  /** Opens the mobile navigation drawer (shown only below the lg breakpoint). */
  onMenuClick?: () => void;
}

export const Header = forwardRef<HTMLElement, HeaderProps>(({ onMenuClick, ...props }, ref) => {
  const [isDark, setIsDark] = useState(true);
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { t } = useTranslation();

  useEffect(() => {
    // The shell already ships `class="dark"`, so this only re-syncs the toggle
    // state if something stripped it (extension, print stylesheet, etc.).
    document.documentElement.classList.add("dark");
    setIsDark(true);
  }, []);

  const toggleTheme = () => {
    setIsDark(!isDark);
    document.documentElement.classList.toggle("dark");
  };

  const handleSignOut = async () => {
    const { error } = await signOut();
    if (error) {
      toast({ variant: "destructive", title: t("header.errorSigningOut"), description: error.message });
    } else {
      toast({ title: t("header.signedOut"), description: t("header.signedOutDesc") });
      navigate("/");
    }
  };

  return (
    <header ref={ref} className="h-16 border-b border-border bg-card/50 backdrop-blur-xs sticky top-0 z-40" {...props}>
      <div className="h-full px-4 sm:px-6 flex items-center gap-2">
        {/* Mobile menu trigger — always visible in the top bar (no floating overlay) */}
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden shrink-0 h-11 w-11"
          onClick={onMenuClick}
          aria-label={t("header.openMenu")}
          aria-haspopup="dialog"
        >
          <Menu className="h-5 w-5" />
        </Button>

        {/* Brand — full lockup on wide screens, compact icon otherwise */}
        <Logo variant="full" to="/" className="hidden" />
        <Logo variant="icon" to="/" className="lg:hidden shrink-0" />

        {/* Desktop mega-menu — comprehensive, crawlable navigation */}
        <div className="hidden 2xl:flex flex-1 min-w-0 justify-center px-2">
          <TopNav />
        </div>

        {/* Spacer keeps actions right-aligned when the mega-menu is hidden */}
        <div className="flex-1 2xl:hidden" />

        {/* Right side actions */}
        <div className="flex items-center gap-0.5 sm:gap-1">
          {/* Site-wide search */}
          <GlobalSearchTrigger />
          <ChatButton />

          {/* Coin balance (signed-in users) */}
          <DailyStreak />
          <CoinBalance />

          {/* Language, timezone and theme live in the mobile drawer below `md`
              so the top bar never crowds on a phone-width frame. */}
          <div className="hidden md:flex items-center gap-1">
            <InstallAppButton variant="ghost" label={uiText("Install")} />
            {/* Language selector */}
            <LanguageErrorBoundary>
              <LanguageSwitcher />
            </LanguageErrorBoundary>

            {/* Timezone selector */}
            <TimezoneSelector variant="compact" />
          </div>

          {/* Show notification bell only for authenticated users */}
          {user && <div className="hidden md:block"><NotificationBell /></div>}

          <Button
            variant="ghost"
            size="icon"
            className="hidden md:inline-flex"
            onClick={toggleTheme}
            aria-label={t("header.toggleTheme")}
          >
            {isDark ? (
              <Sun className="h-5 w-5" />
            ) : (
              <Moon className="h-5 w-5" />
            )}
          </Button>

          {/* Profile — same /profile page the phone bottom bar opens */}
          <Button
            asChild
            variant={location.pathname.startsWith("/profile") ? "secondary" : "outline"}
            size="sm"
            className="hidden md:inline-flex gap-1.5"
          >
            <Link
              to={user ? "/profile" : authPath("/profile")}
              aria-current={location.pathname.startsWith("/profile") ? "page" : undefined}
            >
              <UserRound className="h-4 w-4" aria-hidden="true" />
              <span>{uiText("Profile")}</span>
            </Link>
          </Button>

          {/* Always-visible auth control */}
          {user ? (
            <Button
              variant="outline"
              size="sm"
              className="hidden md:inline-flex gap-1.5"
              onClick={handleSignOut}
              aria-label={t("header.signOut")}
            >
              <LogOut className="h-4 w-4" aria-hidden="true" />
              <span className="hidden sm:inline">{t("header.signOut")}</span>
            </Button>
          ) : (
            <Button asChild variant="default" size="sm" className="gap-1.5">
              <Link to={withUtm(authPath(location))} aria-label={t("header.signIn")}>
                <LogIn className="h-4 w-4" aria-hidden="true" />
                <span className="hidden sm:inline">{t("header.signIn")}</span>
              </Link>
            </Button>
          )}
        </div>
      </div>
    </header>
  );
});

Header.displayName = "Header";
